import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { getStartRoute } from "../../config/locationRegistry";

/**
 * Home — WiMule landing page (Witron × REMA 1000)
 *
 * Cinematic video hero with a glass/neumorphic card, auto-advancing
 * countdown to the dashboard, and a manual "Enter Dashboard" override.
 *
 * Drop hero.mp4 and building.png (poster fallback) into /public.
 * Logos: /public/witron-logo.svg, /public/rema-logo.svg
 */

const VIDEO_LENGTH = 36;   // full hero video length in seconds
const OPENER_DELAY = 1;    // let the fly-into-logo opener play clean
const COUNTDOWN = VIDEO_LENGTH - OPENER_DELAY; // 32s, ends as video finishes
const START_ROUTE = getStartRoute();

/* ── styled-components ─────────────────────────────────────────────── */

const PageContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: right;
  align-items: center;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: var(--color-grey-0, #18212f);
  font-family: "Inter", "Segoe UI", sans-serif;
`;

const Video = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.5;
  filter: saturate(0.72) brightness(0.92);
`;

const overlayIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(24, 33, 47, 0.62) 0%,
    rgba(24, 33, 47, 0.32) 45%,
    rgba(24, 33, 47, 0.82) 100%
  );
  animation: ${overlayIn} 1s 1s both ease-out;
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    ellipse 70% 60% at center,
    transparent 30%,
    rgba(13, 21, 32, 0.55) 100%
  );
  animation: ${overlayIn} 1s 1s both ease-out;
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50%      { opacity: 1;   transform: scale(1.5); }
`;

const Card = styled.div`
  position: relative;
  margin-right: 20%;
  z-index: 10;
  width: min(480px, 90vw);
  padding: 44px 48px;
  border-radius: 20px;
  text-align: center;
  background: rgba(44, 47, 51, 0.55);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.07);
  animation: ${cardIn} 1s 2s both ease-out;
`;

const AppName = styled.p`
  margin: 0 0 20px;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: var(--color-witron-blue, #0e64cb);
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  margin-bottom: 24px;
`;

const Logo = styled.img`
  height: 34px;
  object-fit: contain;
  /* filter: brightness(0) invert(1); */
  opacity: 0.9;
`;

const VDivider = styled.div`
  position: relative;
  width: 1px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(255, 255, 255, 0.22),
    transparent
  );
`;

const VDot = styled.span`
  display: block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  animation: ${pulse} 2.5s ease-in-out infinite;
`;

const Subtitle = styled.p`
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-witron-grey, #939598);
`;

const Location = styled.p`
  margin: 0 0 24px;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: rgba(147, 149, 152, 0.5);
`;

const Hr = styled.div`
  height: 1px;
  margin-bottom: 24px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.08),
    transparent
  );
`;

const BarTrack = styled.div`
  height: 3px;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 10px;
  background: rgba(0, 0, 0, 0.4);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.5);
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: var(--color-witron-blue, #0e64cb);
  box-shadow: 0 0 6px rgba(14, 100, 203, 0.6);
  transition: width 1s linear;
  width: ${(p) => p.$progress}%;
`;

const TimerLabel = styled.p`
  margin: 0 0 24px;
  font-size: 11px;
  letter-spacing: 0.05em;
  color: rgba(147, 149, 152, 0.55);
`;

const EnterButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 26px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.05em;
  cursor: pointer;
  color: var(--color-blue-700, #e0f2fe);
  border: 1px solid rgba(14, 100, 203, 0.35);
  background: linear-gradient(
    145deg,
    rgba(14, 100, 203, 0.16),
    rgba(14, 100, 203, 0.07)
  );
  box-shadow: 3px 3px 8px rgba(0, 0, 0, 0.35),
    -1px -1px 4px rgba(255, 255, 255, 0.03),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(14, 100, 203, 0.6);
    background: linear-gradient(
      145deg,
      rgba(14, 100, 203, 0.28),
      rgba(14, 100, 203, 0.14)
    );
    box-shadow: 4px 4px 12px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.08),
      0 0 16px rgba(14, 100, 203, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.3),
      inset 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const Arrow = styled.span`
  opacity: 0.65;
`;

const Watermark = styled.p`
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  z-index: 10;
  text-align: center;
  font-size: 10px;
  letter-spacing: 0.07em;
  color: rgba(147, 149, 152, 0.28);
`;

/* ── hostname-aware site config (kept from your original pattern) ──── */

function getSiteConfig() {
  const host = window.location.hostname;
  if (host === "remardo.rema.no") {
    return { siteId: "remardo", location: "Rema Distrubusjon Øst · Vinterbro" };
  }
  // vinterbro.wioss.no, localhost, and anything else
  return { siteId: "vinterbro", location: "Rema Distrubusjon Øst · Vinterbro" };
}

/* ── component ─────────────────────────────────────────────────────── */

function HomeLandscape() {
  const navigate = useNavigate();
  const site = getSiteConfig();

  const [seconds, setSeconds] = useState(COUNTDOWN);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return;
    intervalRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          navigate(START_ROUTE, { state: { fromIntro: true } });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [visible, navigate]);

  const progress = ((COUNTDOWN - seconds) / COUNTDOWN) * 100;

  const handleEnter = () => {
    clearInterval(intervalRef.current);
    navigate(START_ROUTE, { state: { fromIntro: true } });
  };

  return (
    <PageContainer>
      <Video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        src="images/hero.mp4"
        poster="images/building.png"
      />
      <Overlay />
      <Vignette />

      <Card style={{ opacity: visible ? 1 : 0 }}>
        <AppName>WiMule</AppName>

        <LogoRow>
          <Logo src="images/WitronLogo.svg" alt="Witron" />
          <VDivider>
            <VDot />
          </VDivider>
          <Logo src="images/RemaLogo.svg" alt="REMA 1000" />
        </LogoRow>

        <Subtitle>Maintenance Operations Platform</Subtitle>
        <Location>{site.location}</Location>

        <Hr />

        <BarTrack>
          <BarFill $progress={progress} />
        </BarTrack>
        <TimerLabel>
          {seconds > 0 ? `Entering WiMule in ${seconds}s` : "Loading…"}
        </TimerLabel>

        <EnterButton onClick={handleEnter}>
          Enter WiMule <Arrow>→</Arrow>
        </EnterButton>
      </Card>

      <Watermark>
        Powered by WIOSS Vinterbro · {new Date().getFullYear()}
      </Watermark>
    </PageContainer>
  );
}

export default HomeLandscape
